import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, addDoc, updateDoc, doc, orderBy, limit, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { DateInput } from './ui';
import { formatarPreco } from '../utils/priceUtils';
import { formatDate } from '../utils/dateUtils';

// Formatar data para dd/mm/yyyy - Encaminha para a função centralizada
const formatarData = formatDate;

const CommissionCalculator = ({ salonId, professionals }) => {
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [selectedProfessional, setSelectedProfessional] = useState('all');
  const [commissions, setCommissions] = useState([]);
  const [rules, setRules] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [selectedItems, setSelectedItems] = useState({});
  const [showDetails, setShowDetails] = useState(false);
  const [periodPreset, setPeriodPreset] = useState('current'); // 'current', 'last', 'custom'
  const [processingPayment, setProcessingPayment] = useState(false);
  const [diagnosticData, setDiagnosticData] = useState({
    hasMissingFields: false
  });
  // Novos estados para o redesign
  const [showFilters, setShowFilters] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferDetails, setTransferDetails] = useState({
    commissionId: null,
    detailIndex: null,
    sourceProfessional: null,
    targetProfessional: null,
    percentage: 100,
    reason: ''
  });
  // Novos estados para o modal de cancelamento
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelDetails, setCancelDetails] = useState({
    commissionId: null,
    detailIndex: null,
    sourceProfessional: null,
    reason: '',
    isNoShow: false,
    motivos: [
      'Cliente desmarcou',
      'Profissional indisponível',
      'Problema técnico',
      'Cliente não compareceu',
      'Reagendamento',
      'Outros'
    ],
    motivoSelecionado: '',
    outroMotivo: ''
  });
  // Novos estados para o ajuste manual de comissões
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [adjustDetails, setAdjustDetails] = useState({
    commissionId: null,
    detailIndex: null,
    sourceProfessional: null,
    originalValue: 0,
    newValue: 0,
    adjustType: 'valor', // 'valor' ou 'percentual'
    adjustValue: 0,
    isBonus: true, // true para aumento, false para redução
    reason: '',
    motivos: [
      'Bônus por excelência',
      'Desconto por atraso',
      'Serviço adicional',
      'Correção de valor',
      'Incentivo mensal',
      'Outros'
    ],
    motivoSelecionado: '',
    outroMotivo: ''
  });
  // Adicionar na parte superior do componente, junto com os outros estados
  const [showAdjustHistory, setShowAdjustHistory] = useState(false);
  const [adjustHistory, setAdjustHistory] = useState([]);

  // Configura datas padrão (mês atual)
  useEffect(() => {
    const today = new Date();
    
    const formatDate = (date) => {
      const d = new Date(date);
      const month = `${d.getMonth() + 1}`.padStart(2, '0');
      const day = `${d.getDate()}`.padStart(2, '0');
      return `${d.getFullYear()}-${month}-${day}`;
    };
    
    // Mês atual
    if (periodPreset === 'current') {
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      setPeriodStart(formatDate(firstDay));
      setPeriodEnd(formatDate(lastDay));
    } 
    // Mês anterior
    else if (periodPreset === 'last') {
      const firstDay = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const lastDay = new Date(today.getFullYear(), today.getMonth(), 0);
    setPeriodStart(formatDate(firstDay));
    setPeriodEnd(formatDate(lastDay));
    }
    // Custom - não alterar
  }, [periodPreset]);

  // Carrega regras de comissão
  useEffect(() => {
    const fetchRules = async () => {
      if (!salonId) return;
      
      try {
        const rulesRef = collection(db, `salons/${salonId}/commissionRules`);
        const rulesQuery = query(rulesRef, where("active", "==", true));
        const rulesSnapshot = await getDocs(rulesQuery);
        
        const rulesData = rulesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setRules(rulesData);
      } catch (error) {
        console.error('Erro ao carregar regras de comissão:', error);
      }
    };
    
    fetchRules();
  }, [salonId]);

  // Função para calcular a comissão de um agendamento específico
  const calcularComissao = (appointment, profissionalId, comissaoPercentual, faturamentoMinimo = 0) => {
    if (!appointment || !profissionalId || appointment.cancelado) return 0;
    
    // Determinar qual profissional considerar para a comissão
    // Se houver profissionalConfirmadoId, usar esse. Caso contrário, usar o profissional original
    const profissionalComissao = appointment.profissionalConfirmadoId || 
      (typeof appointment.profissional === 'object' ? appointment.profissional.id : appointment.profissional);
    
    // Verificar se a comissão é para o profissional correto
    if (profissionalComissao !== profissionalId) return 0;
    
    
    // Verificar se foi pago antecipadamente mas ainda não realizado
    if (appointment.pago && appointment.pagamentoAntecipado && !appointment.profissionalConfirmadoId) return 0;
    
    // Se há um valor customizado, usar este para o cálculo
    const valorBase = appointment.valorCustomizado !== undefined && appointment.valorCustomizado !== null
      ? appointment.valorCustomizado
      : appointment.valorTotal;
    
    // Calcular a comissão
    let valorComissao = valorBase * (comissaoPercentual / 100);
    
    // Aplicar regra de faturamento mínimo se necessário
    if (faturamentoMinimo > 0 && valorBase < faturamentoMinimo) {
      valorComissao = 0;
    }
    
    return valorComissao;
  };

  // Função para buscar dados e calcular comissões
  const calculateCommissions = async () => {
    if (!periodStart || !periodEnd || !salonId) {
      alert('Por favor, selecione um período válido.');
      return;
    }
    
    try {
      setCalculating(true);
      
      // Converter datas para formato de string para compatibilidade com o armazenamento
      const startDateStr = periodStart; // Já está em formato YYYY-MM-DD
      const endDateStr = periodEnd; // Já está em formato YYYY-MM-DD
      
      // Buscar agendamentos no período
      const appointmentsRef = collection(db, `salons/${salonId}/appointments`);

      console.log(`Buscando agendamentos de ${startDateStr} até ${endDateStr}`);

      // Consulta usando string em vez de objeto Date, pois é assim que está armazenado
      let appointmentsQuery;
      
      try {
        appointmentsQuery = query(
          appointmentsRef,
          where('data', '>=', startDateStr),
          where('data', '<=', endDateStr)
        );
      } catch (error) {
        console.error("Erro ao criar consulta com strings:", error);
        
        // Fallback: buscar todos os agendamentos do salão e filtrar manualmente
        appointmentsQuery = query(appointmentsRef);
      }

      console.log("Executando consulta...");
      const snapshot = await getDocs(appointmentsQuery);
      console.log(`Total de agendamentos encontrados: ${snapshot.docs.length}`);

      // Primeiro, vamos logar os dados brutos para análise
      snapshot.docs.forEach((doc, index) => {
        if (index < 5) { // Limitamos a 5 para não sobrecarregar o console
          const data = doc.data();
          console.log(`Agendamento ${index + 1}:`, {
            id: doc.id,
            data: data.data,
            confirmado: data.confirmado,
            profissionalConfirmado: data.profissionalConfirmado,
            pago: data.pago,
            cancelado: data.cancelado,
            profissional: data.profissional,
            cliente: data.cliente,
            servicos: data.servicos
          });
        }
      });

      // Filtragem manual dos agendamentos para aplicar todos os critérios
      const appointmentsData = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(appointment => {
          // Verificar se está dentro do período selecionado
          if (appointmentsQuery === query(appointmentsRef)) {
            if (!appointment.data || 
                appointment.data < startDateStr || 
                appointment.data > endDateStr) {
              return false;
            }
          }
          
          // Filtrar agendamentos cancelados
          if (appointment.cancelado === true) return false;
          
          // Log para depuração
          const dataFormatada = appointment.data || "data não definida";
          
          // Verificar critérios de inclusão
          const isConfirmed = appointment.confirmado === true;
          const isProfessionalConfirmed = appointment.profissionalConfirmado === true;
          const isPaid = appointment.pago === true;
          
          // Verificar o profissional
          let hasProfessional = true;
          if (selectedProfessional !== 'all') {
            const profId = appointment.profissional && 
                          (typeof appointment.profissional === 'string' 
                            ? appointment.profissional 
                            : appointment.profissional.id);
            hasProfessional = profId === selectedProfessional;
          }
          
          // Para comissões pendentes, precisamos de:
          // 1. Agendamento não cancelado (já filtrado acima)
          // 2. Profissional confirmado (atendimento realizado)
          // 3. Pagamento confirmado
          // 4. Profissional correto se filtrado
          const shouldInclude = isProfessionalConfirmed && isPaid && hasProfessional;
          
          // Log para depuração - mostrar todos os registros para entender melhor o problema
          console.log(`Agendamento ${appointment.id} (${dataFormatada}): confirmado=${isConfirmed}, profissionalConfirmado=${isProfessionalConfirmed}, pago=${isPaid}, cancelado=${appointment.cancelado}, profissional correto=${hasProfessional}, incluído=${shouldInclude}`);
          
          return shouldInclude;
        });

      console.log(`Agendamentos filtrados para comissões: ${appointmentsData.length}`);
      
      // Imprimir detalhes das regras de comissão para diagnóstico
      console.log(`Regras de comissão disponíveis (${rules.length}):`);
      rules.forEach((rule, index) => {
        console.log(`Regra ${index + 1}: ${rule.name}, tipo: ${rule.type}, professional: ${rule.professionalId}`);
        if (rule.type === 'service' && rule.serviceRules) {
          console.log(`  Regras de serviço (${rule.serviceRules.length}):`);
          rule.serviceRules.forEach(sr => {
            console.log(`  - Serviço ${sr.serviceName} (${sr.serviceId}): ${sr.percentage}%`);
          });
        }
      });
      
      setAppointments(appointmentsData);
      
      // Se não houver agendamentos válidos, mostrar uma mensagem mais detalhada
      if (appointmentsData.length === 0) {
        // Executar diagnóstico para ajudar o usuário a entender o problema
        const diagnosticResults = await runDiagnostics(snapshot.docs, startDateStr, endDateStr, selectedProfessional);
        
        let diagnosticMessage = '';
        if (diagnosticResults.totalInPeriod > 0) {
          diagnosticMessage = `\n\nDiagnóstico:\n- ${diagnosticResults.totalInPeriod} agendamentos encontrados no período\n- ${diagnosticResults.missingConfirmation} sem confirmação de profissional\n- ${diagnosticResults.missingPayment} sem confirmação de pagamento`;
          
          // Guardar os resultados do diagnóstico para possibilitar o botão de correção emergencial
          setDiagnosticData({
            hasMissingFields: true,
            periodStart: startDateStr,
            periodEnd: endDateStr,
            missingConfirmation: diagnosticResults.missingConfirmation,
            missingPayment: diagnosticResults.missingPayment,
            totalInPeriod: diagnosticResults.totalInPeriod,
            snapshot: snapshot
          });
        } else {
          setDiagnosticData({
            hasMissingFields: false
          });
        }
        
        alert(`Nenhum agendamento encontrado com pagamento e profissional confirmados no período de ${formatarData(startDateStr)} a ${formatarData(endDateStr)}.${diagnosticMessage}\n\nVerifique se os agendamentos têm os campos pago e profissionalConfirmado marcados como true.`);
        setCalculating(false);
        return;
      } else {
        // Limpar diagnóstico se tudo estiver OK
        setDiagnosticData({
          hasMissingFields: false
        });
      }
      
      // Processar agendamentos e calcular comissões
      const commissionsByProfessional = {};
      
      // Inicializar comissões para cada profissional
      professionals.forEach(professional => {
        if (selectedProfessional === 'all' || selectedProfessional === professional.id) {
          commissionsByProfessional[professional.id] = {
            id: professional.id,
            name: professional.nome,
            totalServices: 0,
            totalProducts: 0,
            totalCommission: 0,
            details: []
          };
        }
      });
      
      // Processar cada agendamento
      appointmentsData.forEach(appointment => {
        let professionalId;
        let professionalName;
        
        // Determinar o profissional
        if (typeof appointment.profissional === 'string') {
          professionalId = appointment.profissional;
          professionalName = professionals.find(p => p.id === professionalId)?.nome || 'Desconhecido';
        } else if (appointment.profissional && appointment.profissional.id) {
          professionalId = appointment.profissional.id;
          professionalName = appointment.profissional.nome || professionals.find(p => p.id === professionalId)?.nome || 'Desconhecido';
        } else {
          return; // Pular agendamento sem profissional
        }
        
        // Verificar se o profissional já existe no objeto de comissões
        if (!commissionsByProfessional[professionalId]) {
          if (selectedProfessional !== 'all' && selectedProfessional !== professionalId) {
            return; // Pular se não for o profissional selecionado
          }
          
          commissionsByProfessional[professionalId] = {
            id: professionalId,
            name: professionalName,
            totalServices: 0,
            totalProducts: 0,
            totalCommission: 0,
            details: []
          };
        }
        
        // Referência ao objeto de comissão do profissional
        const profCommission = commissionsByProfessional[professionalId];
        
        // Processar serviços do agendamento
        if (appointment.servicos && Array.isArray(appointment.servicos)) {
          appointment.servicos.forEach(service => {
            let serviceId, serviceName, serviceValue;
            
            // Extrair informações do serviço
            if (typeof service === 'string') {
              serviceId = service;
              serviceName = 'Serviço';
              serviceValue = 0;
              console.log(`Atenção: Serviço ${serviceId} está como string, sem valor definido.`);
            } else if (typeof service === 'object') {
              serviceId = service.id;
              serviceName = service.nome;
              // Verificar tanto 'valor' quanto 'preco', já que ambos são usados em diferentes partes do sistema
              serviceValue = service.valor || service.preco || 0;
              if (!service.valor && !service.preco) {
                console.log(`Atenção: Serviço ${serviceName} (${serviceId}) não tem valor ou preço definido.`);
              } else if (!service.valor && service.preco) {
                console.log(`Informação: Serviço ${serviceName} usa campo 'preco' (${service.preco}) em vez de 'valor'.`);
              }
            } else {
              return; // Serviço inválido
            }
            
            // Encontrar regra de comissão aplicável
            const rule = rules.find(r => 
              // Para regras do tipo 'service', queremos verificar se o profissional corresponde
              (r.professionalId === professionalId || r.professionalId === 'all')
            );
          
            if (!rule) return; // Sem regra aplicável
            
            // Calcular comissão
            let commissionValue = 0;

            // Verificar se existe um valor de comissão ajustado para este serviço
            if (appointment.possuiAjusteComissao === true && 
                appointment.ajustesComissao && 
                appointment.ajustesComissao[serviceId] && 
                typeof appointment.ajustesComissao[serviceId].valorComissaoAjustado === 'number') {
              // Usar o valor já ajustado em vez de recalcular
              commissionValue = appointment.ajustesComissao[serviceId].valorComissaoAjustado;
              console.log(`Usando valor de comissão ajustado para serviço ${serviceName}: ${commissionValue}`);
            } else {
              // Calcular comissão usando a função centralizada
              // Criar objeto temporário de agendamento com o serviço específico para cálculo
              const tempAppointment = {
                ...appointment,
                valorTotal: serviceValue,
                // Usar valorCustomizado se disponível
                valorCustomizado: appointment.valorCustomizado
              };
              
              // Aplicar regras baseadas no tipo de comissão
              if (rule.type === 'percentage') {
                commissionValue = calcularComissao(tempAppointment, professionalId, rule.value);
              } else if (rule.type === 'fixed') {
                commissionValue = rule.value;
              } else if (rule.type === 'simple' && typeof rule.commissionPercentage === 'number') {
                // Regras simples usam commissionPercentage para o percentual padrão
                commissionValue = calcularComissao(tempAppointment, professionalId, rule.commissionPercentage);
                console.log(`Calculando comissão simples para serviço ${serviceName}: ${serviceValue} * ${rule.commissionPercentage}% = ${commissionValue}`);
              } else if (rule.type === 'service' && rule.serviceRules && Array.isArray(rule.serviceRules)) {
                // Encontrar a regra específica para este serviço
                const serviceRule = rule.serviceRules.find(sr => sr.serviceId === serviceId);
                if (serviceRule && typeof serviceRule.percentage === 'number') {
                  // Aplicar o percentual específico do serviço
                  commissionValue = calcularComissao(tempAppointment, professionalId, serviceRule.percentage);
                  console.log(`Calculando comissão para serviço ${serviceName} (${serviceId}): ${serviceValue} * ${serviceRule.percentage}% = ${commissionValue}`);
                } else {
                  console.log(`Regra de serviço encontrada, mas sem percentual definido para o serviço ${serviceId}`);
                }
              } else {
                console.log(`Tipo de regra não suportado ou mal configurado: ${rule.type}`);
              }
            }
            
            // Atualizar totais
            profCommission.totalServices += 1;
            profCommission.totalCommission += commissionValue;
            
            // Adicionar detalhes
            profCommission.details.push({
              appointmentId: appointment.id,
              client: appointment.cliente,
              date: appointment.data,
              type: 'service',
              serviceId,
              serviceName,
              serviceValue,
              commissionValue,
              pago: appointment.pago || false,
              profissionalConfirmado: appointment.profissionalConfirmado || false,
              profissionalConfirmadoId: appointment.profissionalConfirmadoId || professionalId,
              noShow: appointment.noShow || false,
              // Adicionar informações sobre ajustes de comissão
              possuiAjuste: appointment.possuiAjusteComissao && appointment.ajustesComissao && appointment.ajustesComissao[serviceId] || false,
              motivoAjuste: appointment.ajustesComissao && appointment.ajustesComissao[serviceId] ? 
                            appointment.ajustesComissao[serviceId].motivoAjusteComissao : '',
              rule: {
                id: rule.id,
                type: rule.type,
                value: rule.value
              }
            });
          });
        }
      });
      
      // Converter para array e filtrar profissionais sem comissões
      const commissionsArray = Object.values(commissionsByProfessional)
        .filter(comm => comm.totalCommission > 0)
        .sort((a, b) => b.totalCommission - a.totalCommission)
        .map(comm => ({
          ...comm,
          showDetails: false // Inicializar com detalhes ocultos
        }));
      
      setCommissions(commissionsArray);
      
      // Inicializar seleção
      const initialSelection = {};
      commissionsArray.forEach(comm => {
        initialSelection[comm.id] = false;
      });
      setSelectedItems(initialSelection);
      
      setCalculating(false);
    } catch (error) {
      console.error('Erro ao calcular comissões:', error);
      alert('Ocorreu um erro ao calcular as comissões. Por favor, tente novamente.');
      setCalculating(false);
    }
  };

  // Função para pagar todas as comissões selecionadas
  const paySelectedCommissions = async () => {
    const selectedCommissions = commissions.filter(comm => selectedItems[comm.id]);
    
    if (selectedCommissions.length === 0) {
      alert('Por favor, selecione pelo menos um profissional.');
      return;
    }
    
    if (!window.confirm(`Deseja confirmar o pagamento de comissões para ${selectedCommissions.length} profissional(is)?`)) {
      return;
    }
    
    try {
      setProcessingPayment(true);
      
      // Processar cada comissão selecionada
      for (const commission of selectedCommissions) {
        await saveCommissions(commission.id);
      }
      
      alert('Comissões pagas com sucesso!');
      
      // Atualizar lista removendo os itens pagos
      setCommissions(prev => prev.filter(comm => !selectedItems[comm.id]));
      
      // Resetar seleção
      const newSelection = {};
      commissions.forEach(comm => {
        if (!selectedItems[comm.id]) {
          newSelection[comm.id] = false;
        }
      });
      setSelectedItems(newSelection);
      
      setProcessingPayment(false);
    } catch (error) {
      console.error('Erro ao processar pagamentos:', error);
      alert('Ocorreu um erro ao processar os pagamentos.');
      setProcessingPayment(false);
    }
  };

  // Função para salvar uma comissão individual
  const saveCommissions = async (professionalId) => {
      const professional = commissions.find(p => p.id === professionalId);
      if (!professional) return;
    
    // Filtrar detalhes para excluir no-shows e garantir que pagamento e profissional estejam confirmados
    const validDetails = professional.details.filter(detail => 
      !detail.noShow && detail.pago && detail.profissionalConfirmado
    );
    
    // Calcular o total real após filtragem
    const totalValue = validDetails.reduce((sum, detail) => sum + detail.commissionValue, 0);
      
      // Criar um registro de pagamento
      const paymentData = {
        professionalId,
        professionalName: professional.name,
        totalValue: totalValue,
        periodStart: new Date(`${periodStart}T00:00:00`),
        periodEnd: new Date(`${periodEnd}T23:59:59`),
        services: validDetails.length,
        products: professional.totalProducts || 0,
        details: validDetails.map(detail => ({
          ...detail,
          profissionalConfirmadoId: detail.profissionalConfirmadoId || professionalId
        })),
        paymentDate: new Date(),
        createdAt: new Date()
      };
      
      // Salvar no Firestore
      const paymentsRef = collection(db, `salons/${salonId}/commissionPayments`);
      await addDoc(paymentsRef, paymentData);
  };

  // Função para selecionar/deselecionar todos os itens
  const toggleSelectAll = (checked) => {
    const newSelection = {};
    commissions.forEach(comm => {
      newSelection[comm.id] = checked;
    });
    setSelectedItems(newSelection);
  };

  // Função para alternar seleção de um item
  const toggleSelectItem = (id) => {
    setSelectedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Formatar período para exibição
  const formattedPeriod = `${formatarData(periodStart)} a ${formatarData(periodEnd)}`;

  // Renderizar texto do período selecionado
  const renderPeriodText = () => {
    const today = new Date();
    
    if (periodPreset === 'current') {
      const monthName = today.toLocaleString('pt-BR', { month: 'long' });
      return `${monthName} de ${today.getFullYear()}`;
    } else if (periodPreset === 'last') {
      const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const monthName = lastMonth.toLocaleString('pt-BR', { month: 'long' });
      return `${monthName} de ${lastMonth.getFullYear()}`;
    } else {
      return formattedPeriod;
    }
  };

  // Adicione esta função antes do return do componente:
  const updateDetailStatus = (commissionId, detailIndex, field) => {
    setCommissions(prevCommissions => {
      return prevCommissions.map(commission => {
        if (commission.id !== commissionId) return commission;
        
        // Criar cópia do detalhe para modificação
        const updatedDetails = [...commission.details];
        const oldDetail = updatedDetails[detailIndex];
        const newValue = !oldDetail[field];
        
        // Criar novo objeto de detalhe com o campo alterado
        const updatedDetail = {
          ...oldDetail,
          [field]: newValue
        };
        
        // Calcular o novo valor de comissão baseado nos estados
        let newCommissionValue = oldDetail.commissionValue;
        
        // Se estamos atualizando status noShow
        if (field === 'noShow') {
          if (newValue === true) {
            // Marcar como no-show: zerar comissão
            newCommissionValue = 0;
            console.log(`Item ${oldDetail.serviceName}: marcado como no-show, zerando comissão de ${oldDetail.commissionValue} para 0`);
          } else {
            // Desmarcar no-show: restaurar comissão apenas se pagamento confirmado
            if (oldDetail.pago) {
              // Recalcular comissão baseado na regra
              newCommissionValue = recalculateCommission(oldDetail, rules);
              console.log(`Item ${oldDetail.serviceName}: desmarcado como no-show, restaurando comissão para ${newCommissionValue}`);
            }
          }
        }
        
        // Se estamos atualizando status pago
        if (field === 'pago') {
          if (newValue === false) {
            // Desmarcar pagamento: zerar comissão
            newCommissionValue = 0;
            console.log(`Item ${oldDetail.serviceName}: pagamento desmarcado, zerando comissão de ${oldDetail.commissionValue} para 0`);
          } else {
            // Marcar pagamento: restaurar comissão apenas se não for no-show
            if (!oldDetail.noShow) {
              // Recalcular comissão baseado na regra
              newCommissionValue = recalculateCommission(oldDetail, rules);
              console.log(`Item ${oldDetail.serviceName}: pagamento confirmado, restaurando comissão para ${newCommissionValue}`);
            }
          }
        }
        
        // Se estamos atualizando status profissionalConfirmado
        if (field === 'profissionalConfirmado') {
          if (newValue === true) {
            // Se não tiver profissionalConfirmadoId definido, usar o ID da comissão
            if (!updatedDetail.profissionalConfirmadoId) {
              updatedDetail.profissionalConfirmadoId = commission.id;
            }
          } else {
            // Se desmarcar, limpar o profissionalConfirmadoId
            updatedDetail.profissionalConfirmadoId = null;
            // Também zerar a comissão se desmarcar o profissional
            newCommissionValue = 0;
          }
        }
        
        // Aplicar o novo valor de comissão ao detalhe
        updatedDetail.commissionValue = newCommissionValue;
        updatedDetails[detailIndex] = updatedDetail;
        
        // Recalcular o total da comissão para o profissional (importante: calcular do zero)
        const newTotalCommission = updatedDetails.reduce((sum, detail) => sum + detail.commissionValue, 0);
        
        console.log(`Comissão total recalculada para ${commission.name}: ${newTotalCommission}`);
        
        // Retornar objeto commission completamente novo
        return {
          ...commission,
          details: updatedDetails,
          totalCommission: newTotalCommission
        };
      });
    });
  };

  // Função auxiliar para recalcular comissão baseado na regra
  const recalculateCommission = (detail, rules) => {
    let commissionValue = 0;
    const { serviceValue, serviceId, rule } = detail;
    
    if (!rule) return 0;
    
    if (rule.type === 'percentage') {
      commissionValue = serviceValue * (rule.value / 100);
    } else if (rule.type === 'fixed') {
      commissionValue = rule.value;
    } else if (rule.type === 'simple') {
      // Buscar a regra completa para acessar commissionPercentage
      const fullRule = rules.find(r => r.id === rule.id);
      if (fullRule && typeof fullRule.commissionPercentage === 'number') {
        commissionValue = serviceValue * (fullRule.commissionPercentage / 100);
      }
    } else if (rule.type === 'service' && serviceId) {
      // Buscar a regra completa para acessar serviceRules
      const fullRule = rules.find(r => r.id === rule.id);
      if (fullRule && fullRule.serviceRules && Array.isArray(fullRule.serviceRules)) {
        const serviceRule = fullRule.serviceRules.find(sr => sr.serviceId === serviceId);
        if (serviceRule && typeof serviceRule.percentage === 'number') {
          commissionValue = serviceValue * (serviceRule.percentage / 100);
        }
      }
    }
    
    return commissionValue;
  };

  // Função de diagnóstico para ajudar a identificar problemas com agendamentos
  const runDiagnostics = async (docs, startDate, endDate, professionalId) => {
    let totalInPeriod = 0;
    let missingConfirmation = 0;
    let missingPayment = 0;
    
    docs.forEach(doc => {
      const appointment = doc.data();
      
      // Verificar se está no período
      if (appointment.data >= startDate && appointment.data <= endDate && !appointment.cancelado) {
        totalInPeriod++;
        
        // Verificar o profissional se for necessário
        let hasProfessional = true;
        if (professionalId !== 'all') {
          const profId = appointment.profissional && 
                        (typeof appointment.profissional === 'string' 
                          ? appointment.profissional 
                          : appointment.profissional.id);
          hasProfessional = profId === professionalId;
        }
        
        if (hasProfessional) {
          // Verificar campos que poderiam estar faltando
          if (appointment.profissionalConfirmado !== true) {
            missingConfirmation++;
          }
          
          if (appointment.pago !== true) {
            missingPayment++;
          }
        }
      }
    });
    
    return {
      totalInPeriod,
      missingConfirmation,
      missingPayment
    };
  };

  // Função para corrigir campos faltantes de confirmação em agendamentos
  const fixMissingConfirmationFields = async () => {
    // Confirmação de segurança
    if (!diagnosticData || 
        !window.confirm(`Isso atualizará todos os ${diagnosticData.totalInPeriod} agendamentos no período selecionado, definindo profissionalConfirmado e pago como true. Continuar?`)) {
      return;
    }
    
    try {
      const updatedCount = {
        success: 0,
        error: 0
      };
      
      // Implementação: atualizar registros no Firestore
      // Para cada agendamento no período selecionado
      const startDate = periodStart;
      const endDate = periodEnd;
      const salonRef = collection(db, `salons/${salonId}/appointments`);
      
      // Consulta para obter agendamentos no período
      const appointmentsQuery = query(
        salonRef,
        where('data', '>=', startDate),
        where('data', '<=', endDate),
        where('cancelado', '==', false)
      );
      
      const appointmentsSnap = await getDocs(appointmentsQuery);
      
      // Para cada agendamento, atualizar campos se necessário
      for (const appointmentDoc of appointmentsSnap.docs) {
        const appointment = appointmentDoc.data();
        
        // Verificar se precisa atualizar
        const needsUpdate = 
          appointment.profissionalConfirmado !== true ||
          appointment.pago !== true;
        
        if (needsUpdate) {
          try {
            // Criar objeto com campos a atualizar
            const updateData = {
              profissionalConfirmado: true,
              pago: true
            };
            
            // Se profissionalConfirmadoId não estiver definido, defina-o
            if (!appointment.profissionalConfirmadoId && appointment.profissional) {
              updateData.profissionalConfirmadoId = typeof appointment.profissional === 'object' 
                ? appointment.profissional.id 
                : appointment.profissional;
            }
            
            // Atualizar documento
            await updateDoc(
              doc(db, `salons/${salonId}/appointments/${appointmentDoc.id}`),
              updateData
            );
            
            updatedCount.success++;
          } catch (err) {
            console.error(`Erro ao atualizar agendamento ${appointmentDoc.id}:`, err);
            updatedCount.error++;
          }
        }
      }
      
      // Exibir resultados
      alert(`Atualização concluída!\n${updatedCount.success} agendamentos atualizados com sucesso.\n${updatedCount.error} erros.`);
      
      // Refazer o cálculo de comissões
      await calculateCommissions();
    } catch (error) {
      console.error('Erro ao corrigir campos de confirmação:', error);
      alert('Erro ao processar atualizações. Verifique o console para detalhes.');
    }
  };

  // Nova função para iniciar o processo de transferência de comissão
  const handleTransferCommission = (commissionId, detailIndex, sourceProfessional) => {
    setTransferDetails({
      commissionId,
      detailIndex,
      sourceProfessional,
      targetProfessional: null,
      percentage: 100,
      reason: ''
    });
    setShowTransferModal(true);
  };

  // Nova função para completar a transferência de comissão
  const completeTransferCommission = async () => {
    if (!transferDetails.targetProfessional) {
      alert('Por favor, selecione um profissional para transferir a comissão.');
      return;
    }

    try {
      // Identificar a comissão e o detalhe específico
      const commission = commissions.find(c => c.id === transferDetails.commissionId);
      const detail = commission.details[transferDetails.detailIndex];
      
      // Verificar se a comissão e o detalhe foram encontrados
      if (!commission || !detail) {
        throw new Error('Dados da comissão não encontrados');
      }
      
      // Obter referência ao agendamento
      const appointmentRef = doc(db, `salons/${salonId}/appointments`, detail.appointmentId);
      
      // Atualizar o agendamento com o novo profissional
      await updateDoc(appointmentRef, {
        comissaoTransferida: true,
        profissionalOriginal: detail.professionalId,
        profissionalComissao: transferDetails.targetProfessional,
        percentualComissaoTransferida: transferDetails.percentage,
        motivoTransferencia: transferDetails.reason,
        dataTransferencia: new Date()
      });
      
      // Recalcular comissões para refletir a mudança
      alert('Comissão transferida com sucesso!');
      await calculateCommissions(); // Recarregar as comissões atualizadas
      
      // Fechar o modal
      setShowTransferModal(false);
    } catch (error) {
      console.error('Erro ao transferir comissão:', error);
      alert('Erro ao transferir a comissão. Por favor, tente novamente.');
    }
  };

  // Manipulador para alterações nos detalhes da transferência
  const handleTransferChange = (e) => {
    const { name, value } = e.target;
    setTransferDetails(prev => ({
      ...prev,
      [name]: name === 'percentage' ? Number(value) : value
    }));
  };

  // Função para iniciar o processo de cancelamento
  const handleCancelService = (commissionId, detailIndex, sourceProfessional) => {
    setCancelDetails({
      commissionId,
      detailIndex,
      sourceProfessional,
      reason: '',
      isNoShow: false,
      motivos: [
        'Cliente desmarcou',
        'Profissional indisponível',
        'Problema técnico',
        'Cliente não compareceu',
        'Reagendamento',
        'Outros'
      ],
      motivoSelecionado: '',
      outroMotivo: ''
    });
    setShowCancelModal(true);
  };

  // Manipulador para alterações nos detalhes do cancelamento
  const handleCancelChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCancelDetails(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
      // Se selecionou "Cliente não compareceu", já marca como no-show automaticamente
      isNoShow: name === 'motivoSelecionado' && value === 'Cliente não compareceu' ? true : 
                name === 'isNoShow' ? checked : prev.isNoShow
    }));
  };

  // Função para completar o cancelamento
  const completeCancelService = async () => {
    try {
      // Verificar se temos um motivo
      const finalReason = cancelDetails.motivoSelecionado === 'Outros' 
        ? cancelDetails.outroMotivo 
        : cancelDetails.motivoSelecionado;
        
      if (!finalReason || finalReason.trim() === '') {
        alert('Por favor, selecione ou especifique um motivo para o cancelamento.');
        return;
      }
      
      // Identificar a comissão e o detalhe específico
      const commission = commissions.find(c => c.id === cancelDetails.commissionId);
      const detail = commission.details[cancelDetails.detailIndex];
      
      // Verificar se a comissão e o detalhe foram encontrados
      if (!commission || !detail) {
        throw new Error('Dados da comissão não encontrados');
      }
      
      // Obter referência ao agendamento
      const appointmentRef = doc(db, `salons/${salonId}/appointments`, detail.appointmentId);
      
      // Atualizar o agendamento com as informações de cancelamento
      await updateDoc(appointmentRef, {
        cancelado: true,
        motivoCancelamento: finalReason,
        noShow: cancelDetails.isNoShow,
        dataCancelamento: new Date()
      });
      
      // Recalcular comissões para refletir a mudança
      alert('Serviço cancelado com sucesso!');
      await calculateCommissions(); // Recarregar as comissões atualizadas
      
      // Fechar o modal
      setShowCancelModal(false);
    } catch (error) {
      console.error('Erro ao cancelar serviço:', error);
      alert('Erro ao cancelar o serviço. Por favor, tente novamente.');
    }
  };

  // Função para iniciar o processo de ajuste
  const handleAdjustCommission = (commissionId, detailIndex, sourceProfessional) => {
    const detail = sourceProfessional.details[detailIndex];
    const originalValue = detail.commissionValue;
    const serviceValue = detail.serviceValue || 0;

    setAdjustDetails({
      commissionId,
      detailIndex,
      sourceProfessional,
      originalValue,
      serviceValue,
      newValue: originalValue,
      adjustType: 'valor',
      adjustValue: 0,
      isBonus: true,
      reason: '',
      motivos: [
        'Bônus por excelência',
        'Desconto por atraso',
        'Serviço adicional',
        'Correção de valor',
        'Incentivo mensal',
        'Outros'
      ],
      motivoSelecionado: '',
      outroMotivo: ''
    });
    setShowAdjustModal(true);
  };

  // Manipulador para alterações nos detalhes do ajuste
  const handleAdjustChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setAdjustDetails(prev => {
      // Cria uma cópia para fazer modificações
      const newState = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };
      
      // Atualiza o valor final com base no tipo de ajuste
      if (name === 'adjustType' || name === 'adjustValue' || name === 'isBonus') {
        if (newState.adjustType === 'valor') {
          // Ajuste direto no valor
          const adjustment = parseFloat(newState.adjustValue) || 0;
          newState.newValue = newState.isBonus 
            ? newState.originalValue + adjustment 
            : newState.originalValue - adjustment;
        } else {
          // Ajuste percentual - baseado no valor do serviço, não na comissão
          const serviceValue = newState.sourceProfessional?.details[newState.detailIndex]?.serviceValue || 0;
          const percentage = parseFloat(newState.adjustValue) || 0;
          
          // Define diretamente o novo valor como percentual do valor do serviço
          // Ignorando completamente o valor original da comissão
          newState.newValue = serviceValue * (percentage / 100);
        }
        
        // Garante que o valor não seja negativo
        newState.newValue = Math.max(0, newState.newValue);
      }
      
      return newState;
    });
  };

  // Função para completar o ajuste da comissão
  const completeAdjustCommission = async () => {
    try {
      // Verificar se temos um motivo
      const finalReason = adjustDetails.motivoSelecionado === 'Outros' 
        ? adjustDetails.outroMotivo 
        : adjustDetails.motivoSelecionado;
      
      if (!finalReason || finalReason.trim() === '') {
        alert('Por favor, selecione ou especifique um motivo para o ajuste.');
        return;
      }
      
      // Verificar se o valor final é válido
      if (adjustDetails.newValue <= 0 && adjustDetails.isBonus) {
        alert('O valor do ajuste deve ser maior que zero.');
        return;
      }
      
      // Identificar a comissão e o detalhe específico
      const commission = commissions.find(c => c.id === adjustDetails.commissionId);
      const detail = commission.details[adjustDetails.detailIndex];
      
      // Verificar se a comissão e o detalhe foram encontrados
      if (!commission || !detail) {
        throw new Error('Dados da comissão não encontrados');
      }
      
      // Obter referência ao agendamento
      const appointmentRef = doc(db, `salons/${salonId}/appointments`, detail.appointmentId);
      
      // Buscar o documento atual para obter os ajustes existentes
      const appointmentDoc = await getDoc(appointmentRef);
      const appointmentData = appointmentDoc.exists() ? appointmentDoc.data() : {};
      
      // Obter ajustes existentes ou inicializar objeto vazio
      const existingAdjustments = appointmentData.ajustesComissao || {};
      
      // Criar o registro de ajuste para salvar no histórico
      const adjustmentData = {
        appointmentId: detail.appointmentId,
        serviceId: detail.serviceId,
        serviceName: detail.serviceName,
        professionalId: commission.id,
        professionalName: commission.name,
        originalValue: adjustDetails.originalValue,
        newValue: adjustDetails.newValue,
        adjustmentType: adjustDetails.adjustType,
        adjustmentValue: parseFloat(adjustDetails.adjustValue),
        isBonus: adjustDetails.isBonus,
        reason: finalReason,
        date: new Date(),
        userId: "admin" // Idealmente, usar ID do usuário logado
      };
      
      // Salvar o histórico de ajuste no Firestore
      const adjustmentHistoryRef = collection(db, `salons/${salonId}/commissionAdjustments`);
      await addDoc(adjustmentHistoryRef, adjustmentData);
      
      // Combinar ajustes existentes com o novo ajuste
      const updatedAdjustments = {
        ...existingAdjustments,
        [detail.serviceId]: {
          valorComissaoAjustado: adjustDetails.newValue,
          motivoAjusteComissao: finalReason,
          dataAjusteComissao: new Date(),
          idServico: detail.serviceId,
          profissionalId: commission.id
        }
      };
      
      // Atualizar o documento com os ajustes combinados
      await updateDoc(appointmentRef, {
        ajustesComissao: updatedAdjustments,
        possuiAjusteComissao: true
      });
      
      // Atualizar o estado local com o novo valor de comissão
      setCommissions(prev => 
        prev.map(c => {
          if (c.id !== commission.id) return c;
          
          // Atualizar o detalhe específico
          const updatedDetails = [...c.details];
          updatedDetails[adjustDetails.detailIndex] = {
            ...updatedDetails[adjustDetails.detailIndex],
            commissionValue: adjustDetails.newValue,
            possuiAjuste: true,
            motivoAjuste: finalReason
          };
          
          // Recalcular o total da comissão
          const updatedTotalCommission = updatedDetails.reduce(
            (sum, det) => sum + det.commissionValue, 
            0
          );
          
          return {
            ...c,
            details: updatedDetails,
            totalCommission: updatedTotalCommission
          };
        })
      );
      
      alert('Valor da comissão ajustado com sucesso!');
      setShowAdjustModal(false);
    } catch (error) {
      console.error('Erro ao ajustar comissão:', error);
      alert('Erro ao ajustar o valor da comissão. Por favor, tente novamente.');
    }
  };

  // Função para buscar o histórico de ajustes
  const fetchAdjustmentHistory = async () => {
    try {
      const adjustHistoryRef = collection(db, `salons/${salonId}/commissionAdjustments`);
      const historyQuery = query(
        adjustHistoryRef,
        orderBy('date', 'desc'),
        limit(50) // Limitar a 50 registros mais recentes
      );
      
      const snapshot = await getDocs(historyQuery);
      const adjustData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date?.toDate() || new Date() // Converter timestamp para Date
      }));
      
      setAdjustHistory(adjustData);
      setShowAdjustHistory(true);
    } catch (error) {
      console.error('Erro ao buscar histórico de ajustes:', error);
      alert('Erro ao carregar o histórico de ajustes. Por favor, tente novamente.');
    }
  };

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      {/* Cabeçalho minimalista inspirado no X/Instagram */}
      <div className="px-4 py-4 border-b border-gray-100 flex justify-between items-center">
        <h3 className="text-xl font-semibold text-gray-900">
          Comissões
        </h3>
        <button 
          onClick={() => setShowFilters(!showFilters)}
          className="p-2 rounded-full hover:bg-gray-100"
        >
          <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
            </button>
        </div>
        
      {/* Painel de filtros deslizante (estilo Instagram) */}
      {showFilters && (
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 animate-slideDown">
          <div className="space-y-3">
            {/* Botões de período simplificados */}
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Período</label>
              <div className="flex space-x-2 overflow-x-auto pb-1">
                <button
                  type="button"
                  className={`px-3 py-1.5 text-xs rounded-full ${
                    periodPreset === 'current'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-700'
                  }`}
                  onClick={() => setPeriodPreset('current')}
                >
                  Mês Atual
                </button>
                <button
                  type="button"
                  className={`px-3 py-1.5 text-xs rounded-full ${
                    periodPreset === 'last'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-700'
                  }`}
                  onClick={() => setPeriodPreset('last')}
                >
                  Mês Anterior
                </button>
                <button
                  type="button"
                  className={`px-3 py-1.5 text-xs rounded-full ${
                    periodPreset === 'custom'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-700'
                  }`}
                  onClick={() => setPeriodPreset('custom')}
                >
                  Personalizado
                </button>
              </div>
            </div>
            
            {/* Datas personalizadas (apenas visíveis se "Personalizado" estiver selecionado) */}
            {periodPreset === 'custom' && (
              <div className="grid grid-cols-2 gap-2">
                  <div>
                  <label htmlFor="start-date" className="text-xs font-medium text-gray-500 mb-1 block">
                    De
                    </label>
                    <DateInput 
                      id="start-date"
                      value={periodStart}
                      onChange={e => setPeriodStart(e.target.value)}
                    className="w-full text-sm"
                    />
                  </div>
                  <div>
                  <label htmlFor="end-date" className="text-xs font-medium text-gray-500 mb-1 block">
                    Até
                    </label>
                    <DateInput 
                      id="end-date"
                      value={periodEnd}
                      onChange={e => setPeriodEnd(e.target.value)}
                    className="w-full text-sm"
                    />
                  </div>
                </div>
            )}
            
            {/* Seleção de profissional */}
            <div>
              <label htmlFor="professional-select" className="text-xs font-medium text-gray-500 mb-1 block">
                Profissional
              </label>
              <select
                id="professional-select"
                value={selectedProfessional}
                onChange={(e) => setSelectedProfessional(e.target.value)}
                className="block w-full py-1.5 px-3 border border-gray-300 bg-white rounded-md text-sm"
              >
                <option value="all">Todos os profissionais</option>
                {professionals.map(pro => (
                  <option key={pro.id} value={pro.id}>{pro.nome}</option>
                ))}
              </select>
            </div>
            
            <div className="flex justify-end">
            <button
              onClick={calculateCommissions}
                className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 flex items-center"
              disabled={calculating || !periodStart || !periodEnd}
            >
              {calculating ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Calculando...
                </>
                ) : 'Atualizar'}
            </button>
          </div>
        </div>
      </div>
      )}
      
      {/* Resumo das comissões - apenas quando houver dados */}
      {commissions.length > 0 && (
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">{renderPeriodText()}</p>
              <p className="text-lg font-bold text-gray-900 mt-1">
                {formatarPreco(commissions.reduce((sum, comm) => sum + comm.totalCommission, 0))}
              </p>
            </div>
            
            <div className="flex space-x-2">
              <button
                onClick={paySelectedCommissions}
                className="px-3 py-1.5 bg-green-600 text-white text-xs rounded-md hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center"
                disabled={processingPayment || Object.values(selectedItems).filter(v => v === true).length === 0}
              >
                {processingPayment ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-1 h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processando...
                  </>
                ) : 'Pagar Selecionados'}
              </button>
            </div>
          </div>
        </div>
      )}
              
      {/* Lista de comissões - Design inspirado em Instagram/X */}
      {commissions.length > 0 && (
        <div className="divide-y divide-gray-100">
            {commissions.map(commission => (
            <div key={commission.id} className="group">
              {/* Cabeçalho da comissão - Design minimalista */}
              <div className="px-4 py-3 flex items-center">
                    <input
                      type="checkbox"
                      id={`select-${commission.id}`}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      checked={!!selectedItems[commission.id]}
                      onChange={() => toggleSelectItem(commission.id)}
                    />
                    
                {/* Avatar do profissional */}
                <div className="ml-3 w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden">
                  {commission.avatar ? (
                    <img src={commission.avatar} alt={commission.name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-lg font-medium text-gray-500">{commission.name.charAt(0)}</span>
                  )}
                        </div>
                
                <div className="ml-3 flex-grow">
                  <h4 className="font-medium text-gray-900">{commission.name}</h4>
                  <p className="text-xs text-gray-500">{commission.totalServices} serviços</p>
                      </div>
                      
                <div className="text-right">
                  <p className="text-lg font-bold text-gray-900">
                          {formatarPreco(commission.totalCommission)}
                        </p>
                  
                  <div className="flex space-x-2 mt-1">
                    <button
                      onClick={() => saveCommissions(commission.id)}
                      className="text-xs text-green-600 font-medium"
                    >
                      Pagar
                    </button>
                    
                    <button
                      onClick={() => {
                        setCommissions(prev => 
                          prev.map(c => 
                            c.id === commission.id 
                              ? {...c, showDetails: !c.showDetails} 
                              : c
                          )
                        );
                      }}
                      className="text-xs text-blue-600 font-medium"
                    >
                      {commission.showDetails ? 'Ocultar' : 'Detalhes'}
                    </button>
                  </div>
                </div>
                              </div>
              
              {/* Detalhes da comissão - Lista simplificada */}
              {commission.showDetails && (
                <div className="bg-gray-50 px-4 py-2 border-t border-gray-100">
                  <div className="space-y-2 py-1">
                    {commission.details.map((detail, index) => (
                      <div 
                        key={index} 
                        className={`bg-white p-3 rounded border shadow-sm relative overflow-hidden
                                   ${detail.cancelado ? 'border-red-200 bg-red-50' : 'border-gray-100'}`} 
                        id={`detail-${commission.id}-${index}`}
                      >
                        {/* Barra lateral de status */}
                        <div className={`absolute left-0 top-0 bottom-0 w-1 
                                       ${detail.cancelado ? 'bg-red-500' : 
                                         detail.commissionPaid ? 'bg-green-500' : 'bg-blue-500'}`}></div>
                        
                        <div className="flex justify-between">
                              <div>
                            <p className="font-medium text-sm">
                              {detail.serviceName}
                              {detail.comissaoTransferida && (
                                <span className="ml-1 text-xs text-amber-600 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5">
                                  Transferida
                                </span>
                              )}
                              {detail.cancelado && (
                                <span className="ml-1 text-xs text-red-600 rounded-full border border-red-300 bg-red-50 px-2 py-0.5">
                                  Cancelado
                                </span>
                              )}
                              {detail.possuiAjuste && (
                                <span className="ml-1 text-xs text-indigo-600 rounded-full border border-indigo-300 bg-indigo-50 px-2 py-0.5">
                                  Ajustada
                                </span>
                              )}
                              {detail.noShow && (
                                <span className="ml-1 text-xs text-gray-600 rounded-full border border-gray-300 bg-gray-50 px-2 py-0.5">
                                  No-show
                                </span>
                              )}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              {formatarData(detail.date)} • {detail.client}
                            </p>
                            {detail.cancelado && detail.motivoCancelamento && (
                              <p className="text-xs text-red-600 mt-1">
                                Motivo: {detail.motivoCancelamento}
                              </p>
                            )}
                            {detail.possuiAjuste && detail.motivoAjuste && (
                              <p className="text-xs text-indigo-600 mt-1">
                                Ajuste: {detail.motivoAjuste}
                              </p>
                            )}
                              </div>
                          
                            <div className="text-right">
                            <p className={`font-bold text-sm ${detail.cancelado ? 'line-through text-gray-400' : ''}`}>
                              {formatarPreco(detail.commissionValue)}
                            </p>
                            <p className={`text-xs text-gray-500 ${detail.cancelado ? 'line-through' : ''}`}>
                              {formatarPreco(detail.serviceValue)}
                            </p>
                      </div>
                    </div>
                    
                        {/* Ações contextuais - Botões de ação */}
                        <div className="mt-2 pt-2 border-t border-gray-100 flex justify-end space-x-2">
                          <button
                            onClick={() => handleAdjustCommission(commission.id, index, commission)}
                            className="text-xs text-indigo-600 hover:text-indigo-800 font-medium flex items-center"
                            disabled={detail.commissionPaid || detail.cancelado}
                          >
                            <svg className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
                              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                            </svg>
                            Ajustar
                          </button>
                          
                          <button
                            onClick={() => handleTransferCommission(commission.id, index, commission)}
                            className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center"
                            disabled={detail.commissionPaid || detail.comissaoTransferida || detail.cancelado}
                          >
                            <svg className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
                              <path d="M8 5a1 1 0 100 2h5.586l-1.293 1.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L13.586 5H8zM12 15a1 1 0 100-2H6.414l1.293-1.293a1 1 0 10-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L6.414 15H12z" />
                            </svg>
                            Transferir
                          </button>
                          
                          <button
                            onClick={() => handleCancelService(commission.id, index, commission)}
                            className="text-xs text-red-600 hover:text-red-800 font-medium flex items-center"
                            disabled={detail.commissionPaid || detail.comissaoTransferida || detail.cancelado}
                          >
                            <svg className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                            Cancelar
                          </button>
                                    </div>
                                  </div>
                    ))}
                      </div>
                  </div>
                )}
              </div>
            ))}
        </div>
      )}
      
      {/* Estado vazio - Redesenhado */}
      {commissions.length === 0 && !calculating && (
        <div className="text-center py-12 px-4">
          <div className="bg-gray-50 w-16 h-16 mx-auto rounded-full flex items-center justify-center">
            <svg className="h-8 w-8 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M6.672 1.911a1 1 0 10-1.932.518l.259.966a1 1 0 001.932-.518l-.26-.966zM2.429 4.74a1 1 0 10-.517 1.932l.966.259a1 1 0 00.517-1.932l-.966-.26zm8.814-.569a1 1 0 00-1.415-1.414l-.707.707a1 1 0 101.415 1.415l.707-.708zm-7.071 7.072l.707-.707A1 1 0 003.465 9.12l-.708.707a1 1 0 001.415 1.415zm3.2-5.171a1 1 0 00-1.3 1.3l4 10a1 1 0 001.823.075l1.38-2.759 3.018 3.02a1 1 0 001.414-1.415l-3.019-3.02 2.76-1.379a1 1 0 00-.076-1.822l-10-4z" />
            </svg>
          </div>
          
          <h3 className="mt-4 text-sm font-medium text-gray-900">Comissões não calculadas</h3>
          <p className="mt-1 text-sm text-gray-500">
            Filtre pelo período desejado e atualize para ver as comissões pendentes
          </p>
          
          <button
            onClick={() => setShowFilters(true)}
            className="mt-4 px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
          >
            Ver filtros
          </button>
          
          {/* Botão de emergência para corrigir campos faltantes */}
          {diagnosticData.hasMissingFields && (
            <div className="mt-6">
              <button
                onClick={fixMissingConfirmationFields}
                className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors"
              >
                Corrigir {diagnosticData.totalInPeriod} agendamentos
              </button>
              <p className="mt-2 text-xs text-gray-500 max-w-md mx-auto">
                Esta ação corrigirá todos os agendamentos do período que estão confirmados mas sem os campos 
                de confirmação de pagamento ou profissional devidamente marcados.
              </p>
            </div>
          )}
        </div>
      )}
      
      {/* Modal de transferência de comissão */}
      {showTransferModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Transferir Comissão</h3>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Detalhes do serviço */}
              {transferDetails.commissionId && transferDetails.sourceProfessional && (
                <div className="bg-blue-50 p-3 rounded-md text-sm">
                  <p className="font-medium text-blue-800">
                    {transferDetails.sourceProfessional.details[transferDetails.detailIndex].serviceName}
                  </p>
                  <p className="text-blue-700 mt-1">
                    Cliente: {transferDetails.sourceProfessional.details[transferDetails.detailIndex].client}
                  </p>
                  <p className="text-blue-700">
                    Data: {formatarData(transferDetails.sourceProfessional.details[transferDetails.detailIndex].date)}
                  </p>
                  <p className="text-blue-800 font-medium mt-1">
                    Valor da comissão: {formatarPreco(transferDetails.sourceProfessional.details[transferDetails.detailIndex].commissionValue)}
                  </p>
                </div>
              )}
              
              <div>
                <label htmlFor="source-professional" className="block text-sm font-medium text-gray-700 mb-1">
                  De: Profissional atual
                </label>
                <input
                  type="text"
                  id="source-professional"
                  className="block w-full p-2 border border-gray-300 rounded-md bg-gray-100"
                  value={transferDetails.sourceProfessional?.name || ''}
                  disabled
                />
              </div>
              
              <div>
                <label htmlFor="target-professional" className="block text-sm font-medium text-gray-700 mb-1">
                  Para: Novo profissional
                </label>
                <select
                  id="target-professional"
                  name="targetProfessional"
                  value={transferDetails.targetProfessional || ''}
                  onChange={handleTransferChange}
                  className="block w-full p-2 border border-gray-300 rounded-md"
                  required
                >
                  <option value="">Selecione o profissional</option>
                  {professionals
                    .filter(p => p.id !== transferDetails.sourceProfessional?.id)
                    .map(pro => (
                      <option key={pro.id} value={pro.id}>{pro.nome}</option>
                    ))
                  }
                </select>
              </div>
              
              <div>
                <label htmlFor="percentage" className="block text-sm font-medium text-gray-700 mb-1">
                  Porcentagem da comissão a transferir
                </label>
                <div className="flex items-center">
                  <input
                    type="range"
                    id="percentage"
                    name="percentage"
                    min="1"
                    max="100"
                    value={transferDetails.percentage}
                    onChange={handleTransferChange}
                    className="flex-grow mr-2"
                  />
                  <span className="w-12 text-center font-medium">{transferDetails.percentage}%</span>
                </div>
              </div>
              
              <div>
                <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-1">
                  Motivo da transferência
                </label>
                <textarea
                  id="reason"
                  name="reason"
                  value={transferDetails.reason}
                  onChange={handleTransferChange}
                  className="block w-full p-2 border border-gray-300 rounded-md"
                  rows="3"
                  placeholder="Descreva o motivo da transferência..."
                ></textarea>
              </div>
            </div>
            
            <div className="px-4 py-3 bg-gray-50 flex justify-end space-x-2 rounded-b-lg">
              <button
                type="button"
                onClick={() => setShowTransferModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={completeTransferCommission}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Confirmar Transferência
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de cancelamento de serviço */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Cancelar Serviço</h3>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Detalhes do serviço */}
              {cancelDetails.commissionId && cancelDetails.sourceProfessional && (
                <div className="bg-red-50 p-3 rounded-md text-sm">
                  <p className="font-medium text-red-800">
                    {cancelDetails.sourceProfessional.details[cancelDetails.detailIndex].serviceName}
                  </p>
                  <p className="text-red-700 mt-1">
                    Cliente: {cancelDetails.sourceProfessional.details[cancelDetails.detailIndex].client}
                  </p>
                  <p className="text-red-700">
                    Data: {formatarData(cancelDetails.sourceProfessional.details[cancelDetails.detailIndex].date)}
                  </p>
                  <p className="text-red-800 font-medium mt-1">
                    Valor da comissão: {formatarPreco(cancelDetails.sourceProfessional.details[cancelDetails.detailIndex].commissionValue)}
                  </p>
                </div>
              )}
              
              {/* Seleção de motivo pré-definido */}
              <div>
                <label htmlFor="motivo-select" className="block text-sm font-medium text-gray-700 mb-1">
                  Motivo do cancelamento
                </label>
                <select
                  id="motivo-select"
                  name="motivoSelecionado"
                  value={cancelDetails.motivoSelecionado}
                  onChange={handleCancelChange}
                  className="block w-full p-2 border border-gray-300 rounded-md"
                  required
                >
                  <option value="">Selecione um motivo</option>
                  {cancelDetails.motivos.map(motivo => (
                    <option key={motivo} value={motivo}>{motivo}</option>
                  ))}
                </select>
              </div>
              
              {/* Campo para outro motivo (aparece apenas se "Outros" for selecionado) */}
              {cancelDetails.motivoSelecionado === 'Outros' && (
                <div>
                  <label htmlFor="outro-motivo" className="block text-sm font-medium text-gray-700 mb-1">
                    Especifique o motivo
                  </label>
                  <textarea
                    id="outro-motivo"
                    name="outroMotivo"
                    value={cancelDetails.outroMotivo}
                    onChange={handleCancelChange}
                    className="block w-full p-2 border border-gray-300 rounded-md"
                    rows="3"
                    placeholder="Descreva o motivo do cancelamento..."
                    required
                  ></textarea>
                </div>
              )}
              
              {/* Opção no-show (cliente não compareceu) */}
              <div className="flex items-center pt-2">
                <input
                  type="checkbox"
                  id="no-show"
                  name="isNoShow"
                  checked={cancelDetails.isNoShow}
                  onChange={handleCancelChange}
                  className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                />
                <label htmlFor="no-show" className="ml-2 text-sm text-gray-700">
                  Cliente não compareceu (no-show)
                </label>
              </div>
              
              {/* Alerta de impacto na comissão */}
              <div className="mt-3 p-2 bg-yellow-50 border border-yellow-100 rounded text-xs text-yellow-800">
                <p><strong>Atenção:</strong> O cancelamento removerá este serviço do cálculo de comissões.</p>
                <p className="mt-1">Se o cliente já pagou pelo serviço, considere transferir a comissão em vez de cancelar.</p>
              </div>
            </div>
            
            <div className="px-4 py-3 bg-gray-50 flex justify-end space-x-2 rounded-b-lg">
              <button
                type="button"
                onClick={() => setShowCancelModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Voltar
              </button>
              <button
                type="button"
                onClick={completeCancelService}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Confirmar Cancelamento
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de ajuste de comissão */}
      {showAdjustModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Ajustar Valor da Comissão</h3>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Detalhes do serviço */}
              {adjustDetails.commissionId && adjustDetails.sourceProfessional && (
                <div className="bg-indigo-50 p-3 rounded-md text-sm">
                  <p className="font-medium text-indigo-800">
                    {adjustDetails.sourceProfessional.details[adjustDetails.detailIndex].serviceName}
                  </p>
                  <p className="text-indigo-700 mt-1">
                    Profissional: {adjustDetails.sourceProfessional.name}
                  </p>
                  <p className="text-indigo-700">
                    Cliente: {adjustDetails.sourceProfessional.details[adjustDetails.detailIndex].client}
                  </p>
                  <div className="flex justify-between items-center mt-1">
                    <p className="text-indigo-800 font-medium">Valor atual da comissão:</p>
                    <p className="text-indigo-800 font-medium">{formatarPreco(adjustDetails.originalValue)}</p>
                  </div>
                  <div className="flex justify-between items-center mt-1">
                    <p className="text-indigo-800 font-medium">Valor do serviço:</p>
                    <p className="text-indigo-800 font-medium">
                      {formatarPreco(adjustDetails.sourceProfessional.details[adjustDetails.detailIndex].serviceValue)}
                    </p>
                  </div>
                </div>
              )}
              
              {/* Tipo de ajuste */}
              <div className="flex space-x-4">
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="adjust-type-valor"
                    name="adjustType"
                    value="valor"
                    checked={adjustDetails.adjustType === 'valor'}
                    onChange={handleAdjustChange}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                  />
                  <label htmlFor="adjust-type-valor" className="ml-2 text-sm text-gray-700">
                    Valor fixo
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="adjust-type-percentual"
                    name="adjustType"
                    value="percentual"
                    checked={adjustDetails.adjustType === 'percentual'}
                    onChange={handleAdjustChange}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                  />
                  <label htmlFor="adjust-type-percentual" className="ml-2 text-sm text-gray-700">
                    Percentual
                  </label>
                </div>
              </div>
              
              {/* Direção do ajuste (bônus ou desconto) - apenas visível para ajuste de valor fixo */}
              {adjustDetails.adjustType === 'valor' && (
                <div className="flex space-x-4">
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="adjust-bonus"
                      name="isBonus"
                      checked={adjustDetails.isBonus}
                      onChange={() => setAdjustDetails(prev => ({...prev, isBonus: true}))}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300"
                    />
                    <label htmlFor="adjust-bonus" className="ml-2 text-sm text-gray-700">
                      Aumentar
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="adjust-discount"
                      name="isBonus"
                      checked={!adjustDetails.isBonus}
                      onChange={() => setAdjustDetails(prev => ({...prev, isBonus: false}))}
                      className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300"
                    />
                    <label htmlFor="adjust-discount" className="ml-2 text-sm text-gray-700">
                      Reduzir
                    </label>
                  </div>
                </div>
              )}
              
              {/* Campo para valor do ajuste */}
              <div>
                <label htmlFor="adjust-value" className="block text-sm font-medium text-gray-700 mb-1">
                  {adjustDetails.adjustType === 'valor' 
                    ? 'Valor do ajuste (R$)' 
                    : 'Percentual do valor do serviço para definir nova comissão (%)'}
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    id="adjust-value"
                    name="adjustValue"
                    value={adjustDetails.adjustValue}
                    onChange={handleAdjustChange}
                    min="0"
                    step={adjustDetails.adjustType === 'valor' ? '0.01' : '0.1'}
                    className="block w-full py-2 px-3 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder={adjustDetails.adjustType === 'valor' ? '0,00' : '0'}
                  />
                  <span className="ml-2 text-sm text-gray-500">
                    {adjustDetails.adjustType === 'valor' ? 'R$' : '%'}
                  </span>
                </div>
                {adjustDetails.adjustType === 'percentual' && (
                  <p className="mt-1 text-xs text-gray-500">
                    Ao usar percentual, o valor da comissão será definido diretamente como este percentual do valor do serviço.
                  </p>
                )}
              </div>
              
              {/* Resumo do ajuste */}
              <div className="bg-gray-50 p-3 rounded-md border border-gray-200">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-700">Valor original:</p>
                  <p className="text-sm font-medium text-gray-900">{formatarPreco(adjustDetails.originalValue)}</p>
                </div>
                
                {adjustDetails.adjustType === 'percentual' ? (
                  <>
                    <div className="flex justify-between items-center mt-1">
                      <p className="text-sm text-gray-700">Valor do serviço:</p>
                      <p className="text-sm text-gray-600">
                        {formatarPreco(adjustDetails.sourceProfessional?.details[adjustDetails.detailIndex]?.serviceValue || 0)}
                      </p>
                    </div>
                    <div className="flex justify-between items-center mt-1">
                      <p className="text-sm text-gray-700">Percentual aplicado:</p>
                      <p className="text-sm text-gray-600">
                        {parseFloat(adjustDetails.adjustValue) || 0}%
                      </p>
                    </div>
                    <div className="flex justify-between items-center pt-2 mt-2 border-t border-gray-200">
                      <p className="text-sm font-semibold text-gray-900">Novo valor:</p>
                      <p className="text-sm font-bold text-indigo-700">{formatarPreco(adjustDetails.newValue)}</p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between items-center mt-1">
                      <p className="text-sm text-gray-700">
                        {adjustDetails.isBonus ? 'Aumento:' : 'Redução:'}
                      </p>
                      <p className={`text-sm font-medium ${adjustDetails.isBonus ? 'text-green-600' : 'text-red-600'}`}>
                        {adjustDetails.isBonus ? '+' : '-'}
                        {formatarPreco(parseFloat(adjustDetails.adjustValue) || 0)}
                      </p>
                    </div>
                    <div className="flex justify-between items-center pt-2 mt-2 border-t border-gray-200">
                      <p className="text-sm font-semibold text-gray-900">Novo valor:</p>
                      <p className="text-sm font-bold text-indigo-700">{formatarPreco(adjustDetails.newValue)}</p>
                    </div>
                  </>
                )}
              </div>
              
              {/* Seleção de motivo pré-definido */}
              <div>
                <label htmlFor="motivo-ajuste" className="block text-sm font-medium text-gray-700 mb-1">
                  Motivo do ajuste
                </label>
                <select
                  id="motivo-ajuste"
                  name="motivoSelecionado"
                  value={adjustDetails.motivoSelecionado}
                  onChange={handleAdjustChange}
                  className="block w-full p-2 border border-gray-300 rounded-md"
                  required
                >
                  <option value="">Selecione um motivo</option>
                  {adjustDetails.motivos.map(motivo => (
                    <option key={motivo} value={motivo}>{motivo}</option>
                  ))}
                </select>
              </div>
              
              {/* Campo para outro motivo (aparece apenas se "Outros" for selecionado) */}
              {adjustDetails.motivoSelecionado === 'Outros' && (
                <div>
                  <label htmlFor="outro-motivo-ajuste" className="block text-sm font-medium text-gray-700 mb-1">
                    Especifique o motivo
                  </label>
                  <textarea
                    id="outro-motivo-ajuste"
                    name="outroMotivo"
                    value={adjustDetails.outroMotivo}
                    onChange={handleAdjustChange}
                    className="block w-full p-2 border border-gray-300 rounded-md"
                    rows="2"
                    placeholder="Descreva o motivo do ajuste..."
                    required
                  ></textarea>
                </div>
              )}
            </div>
            
            <div className="px-4 py-3 bg-gray-50 flex justify-end space-x-2 rounded-b-lg">
              <button
                type="button"
                onClick={() => setShowAdjustModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={completeAdjustCommission}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              >
                Confirmar Ajuste
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Estado de carregamento - Redesenhado */}
      {calculating && (
        <div className="text-center py-12">
          <div className="bg-blue-50 w-16 h-16 mx-auto rounded-full flex items-center justify-center">
            <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <h3 className="mt-4 text-sm font-medium text-gray-900">Calculando comissões</h3>
          <p className="mt-1 text-sm text-gray-500">
            Isto pode levar alguns segundos...
          </p>
        </div>
      )}
      
      {/* Modal de histórico de ajustes */}
      {showAdjustHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">Histórico de Ajustes de Comissão</h3>
              <button
                onClick={() => setShowAdjustHistory(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            
            <div className="overflow-x-auto">
              {adjustHistory.length > 0 ? (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data</th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Profissional</th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Serviço</th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valor Original</th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valor Final</th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Motivo</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {adjustHistory.map(adjustment => (
                      <tr key={adjustment.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                          {adjustment.date.toLocaleDateString('pt-BR')}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                          {adjustment.professionalName}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                          {adjustment.serviceName}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${adjustment.isBonus ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {adjustment.isBonus ? 'Aumento' : 'Redução'}
                          </span>
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                          {formatarPreco(adjustment.originalValue)}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm font-medium">
                          {formatarPreco(adjustment.newValue)}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-500 max-w-xs truncate">
                          {adjustment.reason}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  Nenhum ajuste de comissão encontrado.
                </div>
              )}
            </div>
            
            <div className="px-4 py-3 bg-gray-50 text-right">
              <button
                onClick={() => setShowAdjustHistory(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommissionCalculator; 